import { lt, pickLocalized } from './localized'
import type {
  Equipment,
  ItemCategory,
  ItemDef,
  ItemRarity,
  ItemStack,
  LanguageCode,
  LocalizedText,
  RecipeIngredient,
} from './types'

export const ITEMS: Record<string, ItemDef> = {
  'food-bread': {
    id: 'food-bread',
    name: lt('Oudbakken brood', 'Stale bread'),
    blurb: lt('Hard, maar het vult.', 'Hard, but it fills you.'),
    glyph: '🍞',
    category: 'food',
    rarity: 'common',
    value: 1,
  },
  'food-canned-meatballs': {
    id: 'food-canned-meatballs',
    name: lt('Blik gehaktballen', 'Can of meatballs'),
    blurb: lt('Onverwoestbaar. Smaak: industrieel.', 'Indestructible. Taste: industrial.'),
    category: 'food',
    rarity: 'common',
    value: 1,
  },
  'food-stamppot': {
    id: 'food-stamppot',
    name: lt('Pot stamppot', 'Pot of stamppot'),
    blurb: lt(
      'Iemand heeft hier nog gekookt. Niet lang geleden.',
      'Someone cooked here. Not long ago.',
    ),
    glyph: '🍲',
    category: 'food',
    rarity: 'uncommon',
    value: 2,
  },
  'food-chocolate': {
    id: 'food-chocolate',
    name: lt('Reep Verkade', 'Verkade chocolate bar'),
    blurb: lt('Donker. Bitter. Onbetaalbaar.', 'Dark. Bitter. Priceless.'),
    category: 'food',
    rarity: 'special',
    value: 4,
  },
  'water-bottle-half': {
    id: 'water-bottle-half',
    name: lt('Halve fles drinkwater', 'Half bottle of water'),
    blurb: lt('Iemand heeft eerlijk gedeeld.', 'Someone shared fairly.'),
    category: 'water',
    rarity: 'common',
    value: 1,
  },
  'water-rain-jerrycan': {
    id: 'water-rain-jerrycan',
    name: lt('Jerrycan regenwater', 'Jerrycan of rainwater'),
    blurb: lt('Gefilterd door een oude theedoek.', 'Filtered through an old tea towel.'),
    category: 'water',
    rarity: 'uncommon',
    value: 2,
  },
  'water-mineral-sealed': {
    id: 'water-mineral-sealed',
    name: lt('Verzegelde Spa Blauw', 'Sealed Spa Blue bottle'),
    blurb: lt('Dop nog dicht. Een wonder.', 'Cap still sealed. A miracle.'),
    glyph: '💧',
    category: 'water',
    rarity: 'special',
    value: 4,
  },
  'meds-paracetamol': {
    id: 'meds-paracetamol',
    name: lt('Verlopen paracetamol', 'Expired paracetamol'),
    blurb: lt('Werkt nog. Min of meer.', 'Still works. More or less.'),
    category: 'meds',
    rarity: 'common',
    value: 1,
  },
  'meds-bandages': {
    id: 'meds-bandages',
    name: lt('Verbandtrommel', 'Bandage tin'),
    blurb: lt('Schoon, droog, half vol.', 'Clean, dry, half full.'),
    category: 'meds',
    rarity: 'uncommon',
    value: 2,
  },
  'meds-antibiotics': {
    id: 'meds-antibiotics',
    name: lt('Antibiotica', 'Antibiotics'),
    blurb: lt('Goud waard. Of meer.', 'Worth gold. Or more.'),
    category: 'meds',
    rarity: 'special',
    value: 5,
  },
  'meds-field-kit': {
    id: 'meds-field-kit',
    name: lt('Veldhospitaal-kit', 'Field hospital kit'),
    blurb: lt(
      'Samengestelde noodset voor snelle behandeling in het veld.',
      'Assembled emergency set for fast treatment in the field.',
    ),
    glyph: '🩹',
    category: 'meds',
    rarity: 'uncommon',
    value: 4,
  },
  'scrap-rusty': {
    id: 'scrap-rusty',
    name: lt('Roestig schroot', 'Rusty scrap'),
    blurb: lt('Kromgetrokken metaal.', 'Bent metal.'),
    category: 'scrap',
    rarity: 'common',
    value: 1,
  },
  'scrap-plank': {
    id: 'scrap-plank',
    name: lt('Plank', 'Plank'),
    blurb: lt('Nog recht genoeg om iets te bouwen.', 'Still straight enough to build with.'),
    category: 'scrap',
    rarity: 'common',
    value: 1,
  },
  'scrap-electronics': {
    id: 'scrap-electronics',
    name: lt('Elektronica-onderdelen', 'Electronic parts'),
    blurb: lt('Werkende printplaten zijn zeldzaam.', 'Working boards are rare.'),
    category: 'scrap',
    rarity: 'uncommon',
    value: 2,
  },
  'scrap-toolkit': {
    id: 'scrap-toolkit',
    name: lt('Gereedschapskist', 'Toolkit'),
    blurb: lt('Compleet. Iemand miste deze.', 'Complete. Someone misses this.'),
    glyph: '🧰',
    category: 'scrap',
    rarity: 'special',
    value: 4,
  },
  'scrap-radio': {
    id: 'scrap-radio',
    name: lt('Veldradio', 'Field radio'),
    blurb: lt(
      'Kraakheldere ontvangst op goede dagen. Op slechte dagen alleen ruis.',
      'Clear reception on good days. Static on bad ones.',
    ),
    glyph: '📻',
    category: 'scrap',
    rarity: 'special',
    value: 6,
  },
  'scrap-radio-broken': {
    id: 'scrap-radio-broken',
    name: lt('Kapotte autoradio', 'Broken car radio'),
    blurb: lt(
      'Printplaat gebarsten — met onderdelen nog te redden.',
      'Board cracked—with parts it might live again.',
    ),
    glyph: '📻',
    category: 'scrap',
    rarity: 'uncommon',
    value: 3,
  },
  'scrap-wire': {
    id: 'scrap-wire',
    name: lt('Koperdraad', 'Copper wire'),
    blurb: lt('Krom en verwrongen, maar nog geleidend.', 'Twisted and kinked, but still conductive.'),
    category: 'scrap',
    rarity: 'common',
    value: 1,
  },
  'food-crackers': {
    id: 'food-crackers',
    name: lt('Droogbeschuit', 'Crispbread'),
    blurb: lt(
      'Houdbaar tot lang na de wereld. Smaakt daarnaar.',
      'Best before long after the world ended. Tastes like it.',
    ),
    category: 'food',
    rarity: 'common',
    value: 1,
  },
  'trinket-lighter': {
    id: 'trinket-lighter',
    name: lt('Goedkope aansteker', 'Disposable lighter'),
    blurb: lt(
      'Nog gas. Iemand heeft hier zorgvuldig mee omgegaan.',
      'Still has gas. Someone was careful with it.',
    ),
    category: 'trinket',
    rarity: 'common',
    value: 3,
  },
  'trinket-soccer-ball': {
    id: 'trinket-soccer-ball',
    name: lt('Lekke voetbal', 'Flat football'),
    blurb: lt('Adidas Telstar. Zucht eruit.', 'Adidas Telstar. Air long gone.'),
    category: 'trinket',
    rarity: 'common',
    value: 2,
  },
  'trinket-rubiks': {
    id: 'trinket-rubiks',
    name: lt('Rubikskubus', "Rubik's cube"),
    blurb: lt('Eén kant geel. De rest hopeloos.', 'One face yellow. The rest hopeless.'),
    category: 'trinket',
    rarity: 'common',
    value: 3,
  },
  'trinket-plush': {
    id: 'trinket-plush',
    name: lt('Pluche olifant', 'Plush elephant'),
    blurb: lt('Heeft op iemands kussen gelegen.', 'Once slept on someone’s pillow.'),
    category: 'trinket',
    rarity: 'uncommon',
    value: 5,
  },
  'trinket-watch': {
    id: 'trinket-watch',
    name: lt('Stilstaand horloge', 'Stopped watch'),
    blurb: lt('Vergulde wijzers. Nog mooi.', 'Gilded hands. Still handsome.'),
    category: 'trinket',
    rarity: 'uncommon',
    value: 8,
  },
  'trinket-vinyl': {
    id: 'trinket-vinyl',
    name: lt('LP "Doe Maar — Skunk"', 'LP "Doe Maar — Skunk"'),
    blurb: lt(
      'Krasvrij. Iemand heeft dit goed bewaard.',
      'Barely scratched. Someone kept it safe.',
    ),
    category: 'trinket',
    rarity: 'special',
    value: 12,
  },
  'trinket-coin': {
    id: 'trinket-coin',
    name: lt('Zilveren euro 2002', 'Silver euro 2002'),
    blurb: lt('Eerste serie. Bijna ongebruikt.', 'First series. Almost unused.'),
    category: 'trinket',
    rarity: 'special',
    value: 15,
  },
  'tool-cooking-pot': {
    id: 'tool-cooking-pot',
    name: lt('Gietijzeren kookpot', 'Cast iron cooking pot'),
    blurb: lt(
      'Zwaar, oud, onverwoestbaar. Doet het op elk vuur.',
      'Heavy, old, indestructible. Works on any flame.',
    ),
    category: 'trinket',
    rarity: 'uncommon',
    value: 4,
  },
  'book-swimming-fundamentals': {
    id: 'book-swimming-fundamentals',
    name: lt('Boek: Zwemmen basis', 'Book: Swimming fundamentals'),
    blurb: lt(
      'Handleiding met droge tekeningen over nat overleven.',
      'A manual with dry diagrams about wet survival.',
    ),
    category: 'trinket',
    rarity: 'uncommon',
    value: 6,
  },
  'book-home-cooking': {
    id: 'book-home-cooking',
    name: lt('Boek: Thuis koken', 'Book: Home cooking'),
    blurb: lt(
      'Basisrecepten en veilige hitte — genoeg voor een echte keuken.',
      'Basics and safe heat — enough to run a real kitchen.',
    ),
    category: 'trinket',
    rarity: 'uncommon',
    value: 6,
  },
  'book-archery-starter': {
    id: 'book-archery-starter',
    name: lt('Boek: Boogschieten voor beginners', 'Book: Archery starter'),
    blurb: lt(
      'Houding, pees, geen paniek. Geschikt voor wilde natuur.',
      'Stance, string, no panic. Built for the wild.',
    ),
    category: 'trinket',
    rarity: 'uncommon',
    value: 6,
  },
  'book-driving-theory': {
    id: 'book-driving-theory',
    name: lt('Boek: Rijtheorie', 'Book: Driving theory'),
    blurb: lt(
      'Verkeersregels uit een wereld met verkeerslichten.',
      'Traffic rules from a world that still had lights.',
    ),
    category: 'trinket',
    rarity: 'uncommon',
    value: 6,
  },
  'book-field-first-aid': {
    id: 'book-field-first-aid',
    name: lt('Boek: Eerste hulp veld', 'Book: Field first aid'),
    blurb: lt(
      'Snelverbanden, nooddoseringen en rustige handen.',
      'Rapid bandaging, emergency doses, and steady hands.',
    ),
    category: 'trinket',
    rarity: 'special',
    value: 8,
  },
  'book-hacking-utrecht': {
    id: 'book-hacking-utrecht',
    name: lt('Boek: Netwerk-infiltratie', 'Book: Network infiltration basics'),
    blurb: lt(
      'SSH, brute kleine routers en voorzichtig loot dumpen.',
      'SSH, polite router prods, and careful loot dumps.',
    ),
    category: 'trinket',
    rarity: 'special',
    value: 8,
  },
  'book-bartering-highway': {
    id: 'book-bartering-highway',
    name: lt('Boek: Ruilen langs de snelweg', 'Book: Bartering along the highway'),
    blurb: lt(
      'Wat mensen geloven over waarde — handiger dan een pistool.',
      'What people believe about value—often handier than a gun.',
    ),
    category: 'trinket',
    rarity: 'uncommon',
    value: 6,
  },
  'computer-tower-salvage': {
    id: 'computer-tower-salvage',
    name: lt('PC-kast wrak', 'Salvaged PC tower'),
    blurb: lt('Voeding dood, moederbord twijfelt.', 'Dead PSU, motherboard hesitant.'),
    category: 'scrap',
    rarity: 'uncommon',
    value: 5,
    glyph: '🖥️',
  },
  'laptop-salvage': {
    id: 'laptop-salvage',
    name: lt('Laptop wrak', 'Salvaged laptop'),
    blurb: lt('Scharnier weg, schijf misschien nog te lezen.', 'Hinge gone—disk maybe readable.'),
    category: 'scrap',
    rarity: 'uncommon',
    value: 4,
    glyph: '💻',
  },
  'raspberry-pi-salvage': {
    id: 'raspberry-pi-salvage',
    name: lt('Mini-board', 'Single-board salvage'),
    blurb: lt(
      'Klein groen board — net genoeg voor een fragiele terminal.',
      'Tiny green board—enough for a fragile terminal.',
    ),
    category: 'scrap',
    rarity: 'special',
    value: 6,
    glyph: '🔌',
  },
  'monitor-salvage': {
    id: 'monitor-salvage',
    name: lt('Monitor wrak', 'Salvaged monitor'),
    blurb: lt('Paneel gebarsten, voeding tikt soms nog.', 'Cracked panel—PSU sometimes ticks.'),
    category: 'scrap',
    rarity: 'common',
    value: 3,
    glyph: '🖵',
  },
  'item-pet-treat': {
    id: 'item-pet-treat',
    name: lt('Dierenlekkers', 'Pet treats'),
    blurb: lt(
      'Hard brokjes — verhoogt het vertrouwen van een metgezel.',
      'Hard biscuits—builds trust with a companion.',
    ),
    category: 'trinket',
    rarity: 'common',
    value: 2,
    glyph: '🦴',
  },
  'pet-companion-stray': {
    id: 'pet-companion-stray',
    name: lt('Zwerfhuisdier', 'Stray companion'),
    blurb: lt(
      'Een gewillige viervoeter — gebruik traktaties om te binden.',
      'A willing stray—use treats to bond.',
    ),
    category: 'trinket',
    rarity: 'special',
    value: 10,
    glyph: '🐕',
  },
  'weapon-bow': {
    id: 'weapon-bow',
    name: lt('Jachtboog', 'Hunting bow'),
    blurb: lt(
      'Stil en herbruikbaar. Vereist training.',
      'Quiet and reusable. Requires training.',
    ),
    category: 'weapon',
    rarity: 'uncommon',
    value: 5,
    weapon: { family: 'bow', dmg: 2 },
  },
  'weapon-pipe': {
    id: 'weapon-pipe',
    name: lt('Loodgietersbuis', 'Lead pipe'),
    blurb: lt('Zwaar genoeg om indruk te maken.', 'Heavy enough to matter.'),
    category: 'weapon',
    rarity: 'common',
    value: 2,
    weapon: { family: 'melee', dmg: 1 },
  },
  'weapon-plank': {
    id: 'weapon-plank',
    name: lt('Houten plank met spijkers', 'Plank with nails'),
    blurb: lt('Grof, maar effectief.', 'Crude, but effective.'),
    category: 'weapon',
    rarity: 'common',
    value: 2,
    weapon: { family: 'melee', dmg: 1 },
  },
  'weapon-tennis-racket': {
    id: 'weapon-tennis-racket',
    name: lt('Tennisracket', 'Tennis racket'),
    blurb: lt(
      'Meer geluid dan schade. Maar het is iets.',
      'More noise than harm. Still something.',
    ),
    category: 'weapon',
    rarity: 'common',
    value: 2,
    weapon: { family: 'melee', dmg: 1 },
  },
}

const RARITY_ORDER: Record<ItemRarity, number> = {
  common: 0,
  uncommon: 1,
  special: 2,
}

export const RARITY_LABELS: Record<ItemRarity, LocalizedText> = {
  common: lt('Algemeen', 'Common'),
  uncommon: lt('Bijzonder', 'Uncommon'),
  special: lt('Zeldzaam', 'Rare'),
}

export function rarityLabel(rarity: ItemRarity, lang: LanguageCode): string {
  return pickLocalized(RARITY_LABELS[rarity], lang)
}

export const CATEGORY_LABELS: Record<ItemCategory, LocalizedText> = {
  food: lt('Voedsel', 'Food'),
  water: lt('Water', 'Water'),
  meds: lt('Medicijnen', 'Meds'),
  scrap: lt('Schroot', 'Scrap'),
  trinket: lt('Waardevol', 'Valuable'),
  weapon: lt('Wapen', 'Weapon'),
}

export function categoryLabel(category: ItemCategory, lang: LanguageCode): string {
  return pickLocalized(CATEGORY_LABELS[category], lang)
}

export const RARITY_COLOR: Record<ItemRarity, string> = {
  common: 'text-stone-300',
  uncommon: 'text-sky-300',
  special: 'text-amber-300',
}

export const RARITY_RING: Record<ItemRarity, string> = {
  common: 'ring-stone-700',
  uncommon: 'ring-sky-800',
  special: 'ring-amber-700',
}

export const CATEGORY_GLYPHS: Record<ItemCategory, string> = {
  food: '🥫',
  water: '💧',
  meds: '💊',
  scrap: '🔩',
  trinket: '✨',
  weapon: '🏹',
}

export function getItemDef(id: string): ItemDef | undefined {
  return ITEMS[id]
}

/** Build loadout equipment from a weapon stash definition (must be `category: weapon`). */
export function equipmentFromWeaponDef(defId: string): Equipment | null {
  const def = ITEMS[defId]
  if (!def || def.category !== 'weapon' || !def.weapon) return null
  return {
    id: `equipped-${defId}`,
    slot: 'weapon',
    name: def.name,
    blurb: def.blurb,
    weaponDefId: defId,
    weaponFamily: def.weapon.family,
    baseDamage: def.weapon.dmg,
  }
}

export function listItemsByCategory(category: ItemCategory): ItemDef[] {
  return Object.values(ITEMS)
    .filter((i) => i.category === category)
    .sort((a, b) => RARITY_ORDER[a.rarity] - RARITY_ORDER[b.rarity])
}

export function countByCategory(
  inventory: ItemStack[],
  category: ItemCategory,
): number {
  return inventory.reduce((sum, stack) => {
    const def = ITEMS[stack.defId]
    return def && def.category === category ? sum + stack.quantity : sum
  }, 0)
}

export function trinketsValue(inventory: ItemStack[]): number {
  return inventory.reduce((sum, stack) => {
    const def = ITEMS[stack.defId]
    return def && def.category === 'trinket'
      ? sum + def.value * stack.quantity
      : sum
  }, 0)
}

export function addItems(
  inventory: ItemStack[],
  additions: ItemStack[],
): ItemStack[] {
  const map = new Map<string, number>()
  for (const s of inventory) map.set(s.defId, (map.get(s.defId) ?? 0) + s.quantity)
  for (const s of additions) {
    if (s.quantity <= 0) continue
    map.set(s.defId, (map.get(s.defId) ?? 0) + s.quantity)
  }
  return Array.from(map.entries())
    .filter(([, q]) => q > 0)
    .map(([defId, quantity]) => ({ defId, quantity }))
}

export function consumeOne(
  inventory: ItemStack[],
  category: ItemCategory,
): { inventory: ItemStack[]; consumed: ItemDef | null } {
  let bestIdx = -1
  let bestRarity = Number.POSITIVE_INFINITY
  for (let i = 0; i < inventory.length; i++) {
    const stack = inventory[i]
    if (stack.quantity <= 0) continue
    if (stack.locked) continue
    const def = ITEMS[stack.defId]
    if (!def || def.category !== category) continue
    const r = RARITY_ORDER[def.rarity]
    if (r < bestRarity) {
      bestRarity = r
      bestIdx = i
    }
  }
  if (bestIdx < 0) return { inventory, consumed: null }

  const stack = inventory[bestIdx]
  const def = ITEMS[stack.defId]
  const newQty = stack.quantity - 1
  const newInventory = [...inventory]
  if (newQty <= 0) {
    newInventory.splice(bestIdx, 1)
  } else {
    newInventory[bestIdx] = { ...stack, quantity: newQty }
  }
  return { inventory: newInventory, consumed: def }
}

export function consumeByDefId(
  inventory: ItemStack[],
  defId: string,
  amount: number,
): ItemStack[] | null {
  if (amount <= 0) return inventory
  const idx = inventory.findIndex((stack) => stack.defId === defId)
  if (idx < 0) return null
  const stack = inventory[idx]
  if (stack.quantity < amount) return null
  const next = [...inventory]
  const nextQuantity = stack.quantity - amount
  if (nextQuantity <= 0) next.splice(idx, 1)
  else next[idx] = { ...stack, quantity: nextQuantity }
  return next
}

export function consumeForRecipe(
  inventory: ItemStack[],
  ingredients: RecipeIngredient[],
): ItemStack[] | null {
  let working = inventory
  for (const ingredient of ingredients) {
    if ('defId' in ingredient) {
      const consumed = consumeByDefId(working, ingredient.defId, ingredient.quantity)
      if (!consumed) return null
      working = consumed
      continue
    }
    for (let i = 0; i < ingredient.quantity; i++) {
      const result = consumeOne(working, ingredient.category)
      if (!result.consumed) return null
      working = result.inventory
    }
  }
  return working
}
